import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaBookmark,
  FaEdit,
  FaEye,
  FaLayerGroup,
  FaPlus,
  FaTimes,
  FaTrash,
  FaTrashAlt,
} from "react-icons/fa";
import EditCollectionModal from "../../../components/profile/EditCollectionModal";
import { useQueryClient } from "@tanstack/react-query";
import { useUserAuth } from "../../../contexts/UserAuthContext";
import { useToast } from "../../../contexts/ToastContext";
import { useCollectionsQuery } from "../../../hooks/profile/queries/useCollectionsQuery";
import { useSavedPostsQuery } from "../../../hooks/profile/queries/useSavedPostsQuery";
import PublicPageLink from "../../../components/common/PublicPageLink";
import ConfirmModal from "../../../components/common/ConfirmModal";
import useBulkSelection from "../../../components/profile/bulk/useBulkSelection";
import BulkSelectBar, {
  SelectCheckbox,
  SelectModeButton,
} from "../../../components/profile/bulk/BulkSelectBar";
import bulkStyles from "../../../components/profile/bulk/BulkSelectBar.module.css";
import runBulkRequests from "../../../utils/runBulkRequests";
import api from "../../../utils/api/axiosInstance";
import getImageUrl from "../../../utils/imageUtils";
import capitalizeFirst from "../../../utils/capitalizeFirst";
import styles from "../../../styles/pages/profile/CollectionsPage.module.css";

/* ─── small helpers ─────────────────────────────────── */

function getPostTitle(post) {
  if (
    (post?.postType === "comparison" || post?.postType === "battle") &&
    post?.comparison
  ) {
    const a = post.comparison?.optionA?.title;
    const b = post.comparison?.optionB?.title;
    const aTitle = a ? capitalizeFirst(String(a)) : null;
    const bTitle = b ? capitalizeFirst(String(b)) : null;
    if (aTitle && bTitle) return `${aTitle} vs ${bTitle}`;
    if (aTitle) return aTitle;
    if (bTitle) return bTitle;
  }

  const products = Array.isArray(post?.products) ? post.products : [];
  if (products.length > 0) {
    const names = products
      .map((p) => (typeof p === "string" ? null : p?.name))
      .filter(Boolean)
      .map((name) => capitalizeFirst(String(name)));
    if (names.length > 0) return names.join(", ");
  }

  if (post?.title?.trim()) return capitalizeFirst(post.title.trim());

  if (post?.caption?.trim()) {
    const caption = post.caption.trim();
    return caption.length > 80 ? `${caption.slice(0, 80)}…` : caption;
  }

  return "Saved post";
}

function getPostThumb(post) {
  if (post?.images?.[0]) return post.images[0];

  const products = Array.isArray(post?.products) ? post.products : [];
  for (const product of products) {
    if (typeof product === "string") continue;
    const productId = product?._id || product?.id;
    const fromMap =
      productId && post?.productImages?.[productId]?.[0];
    if (fromMap) return fromMap;
    if (product?.images?.[0]) return product.images[0];
  }

  return null;
}

function CollectionCard({
  col,
  onClick,
  onDelete,
  onEdit,
  selectMode,
  selected,
  onToggle,
}) {
  const canDelete = col.name !== "General";
  const selecting = selectMode && canDelete;

  return (
    <div
      className={`${styles.colCard} ${selected ? bulkStyles.cardSelected : ""}`}
      onClick={selecting ? () => onToggle(col.name) : onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (selecting) onToggle(col.name);
          else onClick();
        }
      }}
      aria-label={
        selecting ? `Select collection ${col.name}` : `Open collection ${col.name}`
      }
    >
      {selecting && (
        <SelectCheckbox
          className={bulkStyles.cornerCheck}
          checked={selected}
          onChange={() => onToggle(col.name)}
          label={`Select collection ${col.name}`}
        />
      )}
      <div className={styles.colCover}>
        {col.coverImage ? (
          <img src={getImageUrl(col.coverImage)} alt={col.name} className={styles.colCoverImg} />
        ) : (
          <div className={styles.colCoverEmpty}>
            <FaBookmark size={28} />
          </div>
        )}
      </div>
      <div className={styles.colMeta}>
        <span className={styles.colName}>{col.name}</span>
        <span className={styles.colCount}>
          {col.postCount} post{col.postCount === 1 ? "" : "s"}
        </span>
      </div>

      {!selectMode && (
      <button
        type="button"
        className={`preserveTransform ${styles.colEditBtn}${canDelete ? "" : ` ${styles.colEditBtnSolo}`}`}
        title="Edit collection"
        aria-label={`Edit collection ${col.name}`}
        onClick={(e) => {
          e.stopPropagation();
          onEdit(col);
        }}
      >
        <FaEdit size={13} />
      </button>
      )}

      {!selectMode && canDelete && (
        <button
          type="button"
          className={`preserveTransform ${styles.colDeleteBtn}`}
          title="Delete collection"
          aria-label={`Delete collection ${col.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(col.name);
          }}
        >
          <FaTrash size={13} />
        </button>
      )}
    </div>
  );
}

function SavedPostCard({
  post,
  canMove,
  onOpen,
  onUnsave,
  onMove,
  selectMode,
  selected,
  onToggle,
}) {
  const title = getPostTitle(post);
  const thumb = getPostThumb(post);
  const authorName =
    post?.user?.username || post?.user?.name || post?.author?.username || null;

  return (
    <div className={`${styles.savedCard} ${selected ? bulkStyles.cardSelected : ""}`}>
      <div
        className={styles.savedCardLink}
        onClick={() => (selectMode ? onToggle(post._id) : onOpen(post._id))}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (selectMode) onToggle(post._id);
            else onOpen(post._id);
          }
        }}
        role={selectMode ? "button" : "link"}
        aria-pressed={selectMode ? selected : undefined}
        tabIndex={0}
      >
        {thumb ? (
          <img
            src={getImageUrl(thumb)}
            alt={title}
            className={styles.savedImage}
          />
        ) : (
          <div className={styles.savedImagePlaceholder}>
            <FaBookmark size={22} />
          </div>
        )}

        <div className={styles.savedBody}>
          <h3 className={styles.savedName}>{title}</h3>
          {authorName && (
            <span className={styles.savedMeta}>@{authorName}</span>
          )}
        </div>
      </div>

      <div className={styles.savedActions}>
        {selectMode ? (
          <SelectCheckbox
            checked={selected}
            onChange={() => onToggle(post._id)}
            label={`Select ${title}`}
          />
        ) : (
          <>
        <button
          type="button"
          className={styles.savedActionBtn}
          onClick={() => onOpen(post._id)}
          title="View post"
          aria-label="View post"
        >
          <FaEye size={16} />
        </button>
        {canMove && (
          <button
            type="button"
            className={styles.savedActionBtn}
            onClick={() => onMove(post._id)}
            title="Move to collection"
            aria-label="Move to collection"
          >
            <FaLayerGroup size={14} />
          </button>
        )}
        <button
          type="button"
          className={`${styles.savedActionBtn} ${styles.savedActionBtnDanger}`}
          onClick={() => onUnsave(post._id)}
          title="Remove from saved"
          aria-label="Remove from saved"
        >
          <FaTrashAlt size={16} />
        </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── main page ─────────────────────────────────────── */

const CollectionsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useUserAuth();
  const userId = user?.id || user?._id;
  const { showToast } = useToast();

  const [activeCollection, setActiveCollection] = useState(null);
  const [posts, setPosts] = useState([]);

  const [showNewModal, setShowNewModal] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const newNameRef = useRef(null);

  const [movePost, setMovePost] = useState(null);
  const [moving, setMoving] = useState(false);

  const [editingCollection, setEditingCollection] = useState(null);
  const [pendingCollectionNames, setPendingCollectionNames] = useState(null);
  const [pendingPostIds, setPendingPostIds] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const {
    data: collections = [],
    isLoading: collectionsLoading,
    refetch: refetchCollections,
  } = useCollectionsQuery(userId, !!userId);

  const {
    data: fetchedPosts = [],
    isLoading: postsLoading,
  } = useSavedPostsQuery(userId, !!userId && !!activeCollection, activeCollection);

  const deletableNames = useMemo(
    () => collections.filter((col) => col.name !== "General").map((col) => col.name),
    [collections],
  );
  const postIds = useMemo(
    () => posts.map((post) => post._id).filter(Boolean),
    [posts],
  );
  const collectionBulk = useBulkSelection(deletableNames);
  const postBulk = useBulkSelection(postIds);

  useEffect(() => {
    if (activeCollection) setPosts(fetchedPosts);
  }, [fetchedPosts, activeCollection]);

  useEffect(() => {
    if (showNewModal) {
      setNewName("");
      setCreateError("");
      setTimeout(() => newNameRef.current?.focus(), 60);
    }
  }, [showNewModal]);

  const handleOpenCollection = useCallback((name) => {
    collectionBulk.exitSelectMode();
    setActiveCollection(name);
    setPosts([]);
  }, [collectionBulk.exitSelectMode]);

  const handleBack = useCallback(() => {
    postBulk.exitSelectMode();
    setActiveCollection(null);
    setPosts([]);
    refetchCollections();
  }, [postBulk.exitSelectMode, refetchCollections]);

  const handleDeleteCollection = useCallback(
    async (name) => {
      if (!window.confirm(`Delete "${name}"? Posts will be moved to General.`)) return;
      try {
        await api.delete(`/posts/collections/${encodeURIComponent(name)}`);
        queryClient.invalidateQueries(["savedCollections", userId]);
        refetchCollections();
      } catch (err) {
        showToast({
          message: err?.response?.data?.message || "Could not delete collection.",
          type: "error",
        });
      }
    },
    [queryClient, userId, refetchCollections, showToast]
  );

  const handleEditCollection = useCallback((col) => {
    setEditingCollection(col);
  }, []);

  const handleCollectionUpdated = useCallback(
    (updatedCol) => {
      queryClient.setQueryData(["savedCollections", userId], (old = []) =>
        old.map((c) =>
          c.name === editingCollection?.name
            ? { ...c, name: updatedCol.name }
            : c
        )
      );
      setEditingCollection(null);
      refetchCollections();
    },
    [queryClient, userId, editingCollection, refetchCollections]
  );

  const handleCreateCollection = useCallback(
    async (e) => {
      e.preventDefault();
      const trimmed = newName.trim();
      if (!trimmed) { setCreateError("Please enter a name."); return; }
      if (trimmed.length > 60) { setCreateError("Max 60 characters."); return; }
      setCreating(true);
      setCreateError("");
      try {
        await api.post("/posts/collections", { name: trimmed });
        queryClient.invalidateQueries(["savedCollections", userId]);
        refetchCollections();
        setShowNewModal(false);
      } catch (err) {
        setCreateError(err?.response?.data?.message || "Could not create collection.");
      } finally {
        setCreating(false);
      }
    },
    [newName, queryClient, userId, refetchCollections]
  );

  const handleOpenPost = useCallback(
    (postId) => {
      navigate(`/post/${postId}`, {
        state: { from: window.location.pathname + window.location.search },
      });
    },
    [navigate]
  );

  const handleUnsave = useCallback(
    async (postId) => {
      try {
        await api.delete(`/posts/${postId}/save`);
        setPosts((prev) => prev.filter((p) => p._id !== postId));
        queryClient.invalidateQueries(["savedCollections", userId]);
        queryClient.invalidateQueries(["savedPosts", userId]);
        refetchCollections();
      } catch (err) {
        showToast({
          message: err?.response?.data?.message || "Failed to unsave post",
          type: "error",
        });
      }
    },
    [queryClient, userId, refetchCollections, showToast]
  );

  const confirmDeleteCollections = useCallback(async () => {
    const names = pendingCollectionNames || [];
    if (names.length === 0) return;
    setPendingCollectionNames(null);
    setBulkBusy(true);
    try {
      const { succeeded, failed, failedIds } = await runBulkRequests(names, (name) =>
        api.delete(`/posts/collections/${encodeURIComponent(name)}`),
      );
      if (succeeded > 0) {
        queryClient.invalidateQueries(["savedCollections", userId]);
        refetchCollections();
        showToast({
          message:
            succeeded === 1
              ? "Collection deleted"
              : `${succeeded} collections deleted`,
          type: "success",
        });
      }
      if (failed > 0) {
        showToast({
          message: "Some collections could not be deleted",
          type: "error",
        });
        collectionBulk.setSelectedIds(failedIds);
      } else {
        collectionBulk.exitSelectMode();
      }
    } finally {
      setBulkBusy(false);
    }
  }, [
    pendingCollectionNames,
    queryClient,
    userId,
    refetchCollections,
    showToast,
    collectionBulk,
  ]);

  const confirmUnsavePosts = useCallback(async () => {
    const ids = pendingPostIds || [];
    if (ids.length === 0) return;
    setPendingPostIds(null);
    setBulkBusy(true);
    try {
      const { succeeded, failed, failedIds, succeededIds } = await runBulkRequests(
        ids,
        (id) => api.delete(`/posts/${id}/save`),
      );
      if (succeeded > 0) {
        setPosts((prev) => prev.filter((post) => !succeededIds.includes(post._id)));
        queryClient.invalidateQueries(["savedCollections", userId]);
        queryClient.invalidateQueries(["savedPosts", userId]);
        refetchCollections();
        showToast({
          message:
            succeeded === 1
              ? "Removed from saved"
              : `Removed ${succeeded} saved posts`,
          type: "success",
        });
      }
      if (failed > 0) {
        showToast({
          message: "Some posts could not be removed",
          type: "error",
        });
        postBulk.setSelectedIds(failedIds);
      } else {
        postBulk.exitSelectMode();
      }
    } finally {
      setBulkBusy(false);
    }
  }, [
    pendingPostIds,
    queryClient,
    userId,
    refetchCollections,
    showToast,
    postBulk,
  ]);

  const handleMovePost = useCallback((postId) => {
    setMovePost({ postId, currentCollection: activeCollection });
  }, [activeCollection]);

  const handleMoveTo = useCallback(
    async (targetCollection) => {
      if (!movePost) return;
      setMoving(true);
      try {
        await api.patch(`/posts/${movePost.postId}/move-collection`, {
          collectionName: targetCollection,
        });
        setPosts((prev) => prev.filter((p) => p._id !== movePost.postId));
        queryClient.invalidateQueries(["savedCollections", userId]);
        refetchCollections();
        setMovePost(null);
      } catch (err) {
        showToast({
          message: err?.response?.data?.message || "Could not move post.",
          type: "error",
        });
      } finally {
        setMoving(false);
      }
    },
    [movePost, queryClient, userId, refetchCollections, showToast]
  );

  const canMove =
    collections.filter((c) => c.name !== activeCollection).length > 0;

  if (activeCollection) {
    return (
      <div className={styles.page}>
        <div className={styles.detailHeader}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={handleBack}
          >
            <FaArrowLeft size={14} /> All Collections
          </button>
          <div className={styles.headerTop}>
            <h2 className={styles.detailTitle}>
              <FaBookmark size={16} />
              {activeCollection}
            </h2>
            {posts.length > 0 && (
              <SelectModeButton
                active={postBulk.selectMode}
                onClick={postBulk.toggleSelectMode}
                disabled={bulkBusy}
              />
            )}
          </div>
        </div>

        {postBulk.selectMode && (
          <BulkSelectBar
            selectedCount={postBulk.selectedIds.length}
            allSelected={postBulk.allVisibleSelected}
            busy={bulkBusy}
            onSelectAll={postBulk.selectAllVisible}
            onDelete={() => setPendingPostIds(postBulk.selectedIds)}
            deleteLabel="Remove"
            noun="posts"
          />
        )}

        <div className={styles.listWrap}>
          {postsLoading ? (
            <div className={styles.loading}>Loading posts...</div>
          ) : posts.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <FaBookmark size={40} />
              </div>
              <h3>No posts here yet</h3>
              <p>Save posts from home and move them into this collection.</p>
              <PublicPageLink pageKey="feed" to="/community" className={styles.feedLink}>
                Go home
              </PublicPageLink>
            </div>
          ) : (
            <div className={styles.postsGrid}>
              {posts.map((post) => (
                <SavedPostCard
                  key={post._id}
                  post={post}
                  canMove={canMove}
                  onOpen={handleOpenPost}
                  onUnsave={handleUnsave}
                  onMove={handleMovePost}
                  selectMode={postBulk.selectMode}
                  selected={postBulk.selectedIds.includes(post._id)}
                  onToggle={postBulk.toggle}
                />
              ))}
            </div>
          )}
        </div>

        {pendingPostIds && (
          <ConfirmModal
            message={
              pendingPostIds.length === 1
                ? "Remove this post from saved?"
                : `Remove ${pendingPostIds.length} posts from saved?`
            }
            onConfirm={confirmUnsavePosts}
            onClose={() => setPendingPostIds(null)}
          />
        )}

        {movePost && (
          <div className={styles.modalOverlay} onClick={() => setMovePost(null)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <span>Move to collection</span>
                <button
                  type="button"
                  className={styles.modalClose}
                  onClick={() => setMovePost(null)}
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </div>
              <div className={styles.moveList}>
                {collections
                  .filter((c) => c.name !== movePost.currentCollection)
                  .map((c) => (
                    <button
                      key={c.name}
                      type="button"
                      className={styles.moveItem}
                      disabled={moving}
                      onClick={() => handleMoveTo(c.name)}
                    >
                      {c.name}
                      <span className={styles.moveItemCount}>
                        {c.postCount} post{c.postCount === 1 ? "" : "s"}
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <h2 className={styles.title}>
            <FaLayerGroup />
            Collections
          </h2>
          <div className={styles.headerActions}>
            {deletableNames.length > 0 && (
              <SelectModeButton
                active={collectionBulk.selectMode}
                onClick={collectionBulk.toggleSelectMode}
                disabled={bulkBusy}
              />
            )}
            <button
              type="button"
              className={styles.newBtn}
              onClick={() => setShowNewModal(true)}
            >
              <FaPlus size={13} /> New Collection
            </button>
          </div>
        </div>

        {collectionBulk.selectMode && (
          <BulkSelectBar
            selectedCount={collectionBulk.selectedIds.length}
            allSelected={collectionBulk.allVisibleSelected}
            busy={bulkBusy}
            onSelectAll={collectionBulk.selectAllVisible}
            onDelete={() => setPendingCollectionNames(collectionBulk.selectedIds)}
            noun="collections"
          />
        )}
        <p className={styles.subtitle}>
          Your saved posts, organized in named collections.
        </p>
      </div>

      {collectionsLoading ? (
        <div className={styles.loading}>Loading collections...</div>
      ) : collections.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <FaLayerGroup size={40} />
          </div>
          <h3>No collections yet</h3>
          <p>Create a collection to organize the posts you save.</p>
        </div>
      ) : (
        <div className={styles.colGrid}>
          {collections.map((col) => (
            <CollectionCard
              key={col.name}
              col={col}
              onClick={() => handleOpenCollection(col.name)}
              onDelete={handleDeleteCollection}
              onEdit={handleEditCollection}
              selectMode={collectionBulk.selectMode}
              selected={collectionBulk.selectedIds.includes(col.name)}
              onToggle={collectionBulk.toggle}
            />
          ))}
        </div>
      )}

      {showNewModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowNewModal(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <span>New Collection</span>
              <button
                type="button"
                className={styles.modalClose}
                onClick={() => setShowNewModal(false)}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleCreateCollection} className={styles.newForm}>
              <input
                ref={newNameRef}
                type="text"
                className={styles.newInput}
                placeholder="Collection name"
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value);
                  setCreateError("");
                }}
                maxLength={60}
              />
              {createError && (
                <p className={styles.createError}>{createError}</p>
              )}
              <button
                type="submit"
                className={styles.createBtn}
                disabled={creating || !newName.trim()}
              >
                {creating ? "Creating..." : "Create"}
              </button>
            </form>
          </div>
        </div>
      )}

      {pendingCollectionNames && (
        <ConfirmModal
          message={
            pendingCollectionNames.length === 1
              ? `Delete "${pendingCollectionNames[0]}"? Posts will be moved to General.`
              : `Delete ${pendingCollectionNames.length} collections? Posts will be moved to General.`
          }
          onConfirm={confirmDeleteCollections}
          onClose={() => setPendingCollectionNames(null)}
        />
      )}

      {editingCollection && (
        <EditCollectionModal
          collection={editingCollection}
          userId={userId}
          onClose={() => setEditingCollection(null)}
          onUpdated={handleCollectionUpdated}
        />
      )}
    </div>
  );
};

export default CollectionsPage;
